import { ShieldCheck, CalendarClock, Award } from "lucide-react";
import { PROOF_POINTS, SAFETY_PILLARS, TESTIMONIALS } from "../lib/site";
import { Reveal } from "../components/Reveal";
import { RequestQuoteButton, LeaveReviewButton } from "../components/Buttons";

const ICONS = { ShieldCheck, CalendarClock, Award };

export default function Safety() {
  return (
    <div data-testid="safety-page" className="bg-bb-bg">
      {/* Signature banner (hero pull-quote) */}
      <section
        data-testid="safety-signature-banner"
        className="relative grain overflow-hidden bg-bb-bg text-center pt-36 md:pt-44 pb-20 md:pb-28"
      >
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,rgba(201,160,53,0.08),transparent_60%)]" />
        <div className="relative z-10 max-w-4xl mx-auto px-5 md:px-10">
          <Reveal>
            <p className="font-display text-bb-silver text-4xl sm:text-6xl md:text-7xl leading-[1.05]">
              &ldquo;When there are problems, Big Buck stands with you.&rdquo;
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-8 text-bb-gold font-cond uppercase tracking-widest text-base md:text-lg">
              Quatro Osos E&P, LLC
            </p>
          </Reveal>
        </div>
      </section>

      {/* Thin gold divider */}
      <div className="h-px w-full gold-line" data-testid="banner-divider" />

      {/* Section 1: Intro block */}
      <section
        data-testid="track-record-intro"
        className="py-20 md:py-28"
      >
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <Reveal>
            <p className="text-bb-gold font-mono2 text-xs uppercase tracking-mega mb-6">
              [ Track Record ]
            </p>
            <h1 className="font-display uppercase text-4xl sm:text-5xl md:text-6xl text-bb-silver leading-[0.95] mb-8">
              Our Track Record
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-bb-silver/75 text-lg md:text-xl leading-relaxed">
              Big Buck Services has built its reputation the only way that counts
              in the oilfield: by showing up, meeting deadlines, and standing with
              its clients when it matters most. The following comes directly from
              the operators who depend on us.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Section 2: Client testimonials */}
      <section data-testid="testimonials" className="pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={i} delay={i * 0.1} className="h-full">
                <div
                  data-testid={`testimonial-${i}`}
                  className="relative h-full bg-bb-surface border border-white/10 p-8 md:p-9 pl-9 md:pl-11 hover:border-bb-gold/40 transition-colors duration-500"
                >
                  <span className="absolute left-0 top-0 bottom-0 w-1 bg-bb-gold" />
                  <div className="mb-6">
                    <h3 className="font-display uppercase text-2xl text-bb-silver leading-tight">
                      {t.company}
                    </h3>
                    <p className="mt-1 text-bb-gold font-cond uppercase tracking-wide text-sm">
                      {t.person}
                    </p>
                  </div>
                  <p className="text-bb-silver/75 leading-relaxed">{t.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Proof point stats */}
      <section className="py-20 md:py-28 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {PROOF_POINTS.map((p, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div
                  data-testid={`proof-point-${i}`}
                  className="bg-bb-bg p-8 md:p-10 h-full"
                >
                  <div className="font-display text-bb-gold text-5xl md:text-7xl leading-none mb-4">
                    {p.stat}
                  </div>
                  <p className="text-bb-silver/60 text-sm leading-relaxed">
                    {p.label}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-20 md:py-28 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <Reveal>
            <p className="text-bb-gold font-mono2 text-xs uppercase tracking-mega mb-5">
              [ How We Operate ]
            </p>
            <h2 className="font-display uppercase text-4xl md:text-6xl text-bb-silver leading-none mb-16">
              Built on reliability
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {SAFETY_PILLARS.map((p, i) => {
              const Icon = ICONS[p.icon];
              return (
                <Reveal key={p.title} delay={i * 0.1}>
                  <div
                    data-testid={`safety-pillar-${i}`}
                    className="group bg-bb-surface border border-white/10 p-8 md:p-10 h-full hover:border-bb-gold/50 transition-colors duration-500"
                  >
                    <Icon className="h-10 w-10 text-bb-gold mb-8" strokeWidth={1.5} />
                    <h3 className="font-display uppercase text-2xl text-bb-silver mb-4">
                      {p.title}
                    </h3>
                    <p className="text-bb-silver/60 leading-relaxed">{p.copy}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 border-t border-white/10 text-center">
        <div className="max-w-3xl mx-auto px-5 md:px-10">
          <Reveal>
            <h2 className="font-display uppercase text-4xl md:text-6xl text-bb-silver leading-[0.95] mb-8">
              Put us to work.
            </h2>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <RequestQuoteButton size="lg" testid="safety-request-quote-btn" />
              <LeaveReviewButton size="lg" testid="safety-leave-review-btn" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
